import { useEffect, useState } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { ClientProps } from "../../interfaces/interfaces";
import styles from "../components.module.sass";
import { TitleComponent } from "../ui/TitleComponent";

export interface AdditionalDocumentFormValues {
  client: ClientProps;
  customName: string;
  date?: string;
  file?: File | null;
}

interface Props {
  clientOptions: ClientProps[];
  initialValues?: Partial<AdditionalDocumentFormValues>;
  requireFile?: boolean;
  onCancel: () => void;
  onSubmit: (values: AdditionalDocumentFormValues) => void;
}

const emptyClient = { id: 0, name: "" };

export const AdditionalDocumentForm = ({
  clientOptions,
  initialValues,
  requireFile = true,
  onCancel,
  onSubmit,
}: Props) => {
  const [values, setValues] = useState<AdditionalDocumentFormValues>({
    client: emptyClient,
    customName: "",
    date: "",
    file: null,
  });

  useEffect(() => {
    if (!initialValues) return;
    setValues((current) => ({
      ...current,
      ...initialValues,
      file: null,
    }));
  }, [initialValues]);

  const canSave = Boolean(
    values.client?.id &&
      values.customName.trim() &&
      (!requireFile || values.file),
  );

  return (
    <div className={styles.customToolForm}>
      <TitleComponent title="Additional Document" />
      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <TextField
            className={styles.formFieldModalCustom}
            label="Client"
            onChange={(event) => {
              const client =
                clientOptions.find(
                  (option) => String(option.id) === event.target.value,
                ) ?? emptyClient;
              setValues((current) => ({ ...current, client }));
            }}
            select
            SelectProps={{ native: true }}
            value={values.client?.id || ""}
          >
            <option value="" disabled>
              Select a client...
            </option>
            {clientOptions.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item lg={12} xs={12}>
          <TextField
            className={styles.formFieldModalCustom}
            label="Document name"
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                customName: event.target.value,
              }))
            }
            value={values.customName}
          />
        </Grid>
        <Grid item lg={12} xs={12}>
          <TextField
            className={styles.formFieldModalCustom}
            label="Date"
            onChange={(event) =>
              setValues((current) => ({ ...current, date: event.target.value }))
            }
            type="date"
            value={values.date || ""}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item lg={12} xs={12}>
          <input
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            className={styles.formFieldModalCustom}
            name="file"
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                file: event.target.files?.[0] ?? null,
              }))
            }
            type="file"
          />
        </Grid>
      </Grid>
      <div className={styles.buttonGral}>
        <Button onClick={onCancel} variant="contained">
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          onClick={() => onSubmit(values)}
          variant="contained"
        >
          Save
        </Button>
      </div>
    </div>
  );
};
